import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Tabs,
  Typography,
  Space,
  DatePicker,
  Button,
  Spin,
  message,
  Badge,
  Statistic,
  Progress,
  Tooltip,
} from 'antd';
import {
  DashboardOutlined,
  AlertOutlined,
  SettingOutlined,
  MonitorOutlined,
  DownloadOutlined,
  MailOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  FlagOutlined,
  UserOutlined,
  FileExcelOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { PageHeader } from '@ant-design/pro-components';
import { Pie, Column, Line, DualAxes } from '@ant-design/plots';
import dayjs from 'dayjs';
import type { RangePickerProps } from 'antd/es/date-picker';

import { AppDispatch, RootState } from '../../store/store';
import {
  getSTRDashboardStatsThunk,
  getSTRDailySummaryThunk,
  getAllSTRRulesThunk,
  getSTRAlertsThunk,
  downloadSTRExcelReportThunk,
  sendSTRExcelEmailThunk,
  sendSTRDailySummaryEmailThunk,
} from '../../services/strService/strThunk';
import {
  setSelectedDateRange,
  setSelectedFilters,
  setPagination,
  clearAccountMonitoring,
  clearAccountFlags,
} from '../../features/str/strSlice';

import STRAlertManagement from './components/STRAlertManagement';
import STRRuleConfiguration from './components/STRRuleConfiguration';
import STRAccountMonitoring from './components/STRAccountMonitoring';
import STRPerformanceMonitoring from './components/STRPerformanceMonitoring';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const STRDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { dashboardStats, dailySummary, rules, alerts, ui } = useSelector(
    (state: RootState) => state.str
  );

  const [activeTab, setActiveTab] = useState('dashboard');
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setIsRefreshing(true);
      const today = dayjs().format('YYYY-MM-DD');
      const lastWeek = dayjs().subtract(7, 'days').format('YYYY-MM-DD');

      await Promise.all([
        dispatch(getSTRDashboardStatsThunk({ dateFrom: lastWeek, dateTo: today })),
        dispatch(getSTRDailySummaryThunk(today)),
        dispatch(getAllSTRRulesThunk()),
        dispatch(
          getSTRAlertsThunk({
            limit: 10,
            offset: 0,
            dateFrom: lastWeek,
            dateTo: today,
          })
        ),
      ]);
    } catch (error) {
      message.error('Failed to load dashboard data');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleTabChange = (tabKey: string) => {
    // Clear account monitoring state when switching to monitoring tab
    if (tabKey === 'monitoring') {
      dispatch(clearAccountMonitoring());
      dispatch(clearAccountFlags());
    }
    setActiveTab(tabKey);
  };

  const handleDateRangeChange: RangePickerProps['onChange'] = (dates) => {
    if (dates) {
      const range: [string, string] = [
        dates[0]!.format('YYYY-MM-DD'),
        dates[1]!.format('YYYY-MM-DD'),
      ];
      dispatch(setSelectedDateRange(range));
      dispatch(
        getSTRDashboardStatsThunk({
          dateFrom: range[0],
          dateTo: range[1],
        })
      );
    } else {
      dispatch(setSelectedDateRange(null));
    }
  };

  const handleExportExcel = async () => {
    try {
      const dateRange = ui.selectedDateRange || [
        dayjs().subtract(7, 'days').format('YYYY-MM-DD'),
        dayjs().format('YYYY-MM-DD'),
      ];

      await dispatch(
        downloadSTRExcelReportThunk({
          dateFrom: dateRange[0],
          dateTo: dateRange[1],
          includeHidden: true,
          includeResolved: true,
        })
      );
    } catch (error) {
      message.error('Failed to export Excel report');
    }
  };

  const handleSendEmail = async () => {
    try {
      const dateRange = ui.selectedDateRange || [
        dayjs().subtract(7, 'days').format('YYYY-MM-DD'),
        dayjs().format('YYYY-MM-DD'),
      ];

      await dispatch(
        sendSTRExcelEmailThunk({
          reportType: 'alerts',
          dateFrom: dateRange[0],
          dateTo: dateRange[1],
          recipients: ['hansheng.liang@arkmind.com.my', 'manager@redcash.com'],
          subject: `STR Alerts Report - ${dateRange[0]} to ${dateRange[1]}`,
          includeHidden: true,
          includeResolved: true,
        })
      );
    } catch (error) {
      message.error('Failed to send email report');
    }
  };

  const handleSendDailySummary = async () => {
    try {
      const today = dayjs().format('YYYY-MM-DD');
      await dispatch(
        sendSTRDailySummaryEmailThunk({
          date: today,
          recipients: ['hansheng.liang@arkmind.com.my', 'manager@redcash.com'],
        })
      );
    } catch (error) {
      message.error('Failed to send daily summary');
    }
  };

  const renderOverviewCards = () => {
    const stats = dashboardStats.data;
    if (!stats) return null;

    const cards = [
      {
        title: 'Total Alerts',
        value: stats.totalAlerts,
        color: '#1890ff',
        icon: <AlertOutlined />,
        trend: stats.totalAlerts > 0 ? 'up' : 'stable',
      },
      {
        title: 'Active Alerts',
        value: stats.activeAlerts,
        color: '#f5222d',
        icon: <ExclamationCircleOutlined />,
        trend: stats.activeAlerts > 0 ? 'up' : 'stable',
      },
      {
        title: 'Resolved Alerts',
        value: stats.resolvedAlerts,
        color: '#52c41a',
        icon: <CheckCircleOutlined />,
        trend: stats.resolvedAlerts > 0 ? 'up' : 'stable',
      },
      {
        title: 'Hidden Alerts',
        value: stats.hiddenAlerts,
        color: '#faad14',
        icon: <EyeInvisibleOutlined />,
        trend: stats.hiddenAlerts > 0 ? 'up' : 'stable',
      },
      {
        title: 'Monitored Accounts',
        value: stats.totalMonitoredAccounts,
        color: '#13c2c2',
        icon: <UserOutlined />,
        trend: 'stable',
      },
      {
        title: 'Avg Processing Time',
        value: `${stats.averageProcessingTime}ms`,
        color: '#722ed1',
        icon: <ClockCircleOutlined />,
        trend: 'stable',
      },
      {
        title: 'Disabled Accounts',
        value: stats.disabledAccounts,
        color: '#fa8c16',
        icon: <MonitorOutlined />,
        trend: stats.disabledAccounts > 0 ? 'up' : 'stable',
      },
    ];

    return (
      <Row gutter={[12, 12]}>
        {cards.map((card, index) => (
          <Col key={index} xs={24} sm={12} md={8} lg={6}>
            <Card
              bordered={false}
              className="shadow-sm hover:shadow-md transition-shadow duration-200"
              bodyStyle={{ padding: '16px' }}
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className="p-2 rounded-lg flex items-center justify-center"
                  style={{
                    backgroundColor: `${card.color}15`,
                    color: card.color,
                    minWidth: '40px',
                    height: '40px',
                  }}
                >
                  {card.icon}
                </div>
                {card.trend === 'up' && <ArrowUpOutlined className="text-red-500 text-sm" />}
                {card.trend === 'down' && <ArrowDownOutlined className="text-green-500 text-sm" />}
              </div>
              <div>
                <div className="text-2xl font-bold mb-1" style={{ color: card.color }}>
                  {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
                </div>
                <div className="text-sm text-gray-600 leading-tight">{card.title}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderAlertsByRule = () => {
    const stats = dashboardStats.data;
    if (!stats || !stats.alertsByRule) return null;

    const data = stats.alertsByRule.map((item: any) => ({
      rule: item.ruleCode,
      count: item.count,
    }));

    const config = {
      data,
      xField: 'rule',
      yField: 'count',
      color: '#1890ff',
      label: {
        position: 'top' as const,
        style: {
          fill: '#1890ff',
          fontSize: 12,
          fontWeight: 'bold',
        },
      },
      xAxis: {
        label: {
          autoRotate: true,
          autoHide: true,
          style: {
            fontSize: 10,
          },
        },
      },
    };

    return (
      <Card
        title="Alerts by Rule"
        bordered={false}
        className="shadow-sm hover:shadow-md transition-shadow duration-200"
        bodyStyle={{ padding: '16px' }}
      >
        <Column {...config} height={250} />
      </Card>
    );
  };

  const renderAlertsBySeverity = () => {
    const stats = dashboardStats.data;
    if (!stats || !stats.alertsBySeverity) return null;

    const data = stats.alertsBySeverity.map((item: any) => ({
      severity: item.severity,
      count: item.count,
    }));

    const colors = {
      HIGH: '#f5222d',
      MEDIUM: '#faad14',
      LOW: '#52c41a',
    };

    const config = {
      data,
      angleField: 'count',
      colorField: 'severity',
      radius: 0.8,
      label: {
        type: 'outer',
        content: '{name} ({percentage})',
      },
      color: data.map((item) => colors[item.severity as keyof typeof colors] || '#1890ff'),
      interactions: [
        {
          type: 'element-selected',
        },
        {
          type: 'element-active',
        },
      ],
    };

    return (
      <Card
        title="Alerts by Severity"
        bordered={false}
        className="shadow-sm hover:shadow-md transition-shadow duration-200"
        bodyStyle={{ padding: '16px' }}
      >
        <Pie {...config} height={250} />
      </Card>
    );
  };

  const renderAlertsTrend = () => {
    const stats = dashboardStats.data;
    if (!stats || !stats.alertsByDay) return null;

    const data = stats.alertsByDay.map((item) => ({
      date: item.date,
      count: item.count,
    }));

    const config = {
      data,
      xField: 'date',
      yField: 'count',
      color: '#1890ff',
      point: {
        size: 5,
        shape: 'diamond',
      },
      smooth: true,
      xAxis: {
        label: {
          formatter: (text: string) => dayjs(text).format('MM/DD'),
        },
      },
    };

    return (
      <Card
        title="Alerts Trend (Last 7 Days)"
        bordered={false}
        className="shadow-sm hover:shadow-md transition-shadow duration-200"
        bodyStyle={{ padding: '16px' }}
      >
        <Line {...config} height={250} />
      </Card>
    );
  };

  const renderTopAlertingAccounts = () => {
    const stats = dashboardStats.data;
    if (!stats || !stats.topAlertingAccounts) return null;

    return (
      <Card title="Top Alerting Accounts" bordered={false} className="shadow-sm">
        <div className="space-y-3">
          {stats.topAlertingAccounts.slice(0, 5).map((account, index) => (
            <div key={account.agreementId} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Badge count={index + 1} style={{ backgroundColor: '#1890ff' }} />
                <div>
                  <Text strong>{account.customerName}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {account.agreementId}
                  </Text>
                </div>
              </div>
              <div className="text-right">
                <Text strong style={{ color: '#f5222d' }}>
                  {account.alertCount} alerts
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Last: {dayjs(account.lastAlertDate).format('MM/DD HH:mm')}
                </Text>
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  const renderDashboardTab = () => (
    <div className="space-y-4">
      {/* Overview Cards */}
      {renderOverviewCards()}

      {/* Charts Row */}
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={12}>
          {renderAlertsByRule()}
        </Col>
        <Col xs={24} lg={12}>
          {renderAlertsBySeverity()}
        </Col>
      </Row>

      {/* Trend and Top Accounts */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          {renderAlertsTrend()}
        </Col>
        <Col xs={24} lg={8}>
          {renderTopAlertingAccounts()}
        </Col>
      </Row>

      {/* Rules Status */}
      <Card title="STR Rules Status" bordered={false} className="shadow-sm">
        <Row gutter={[16, 16]}>
          {rules?.data?.map((rule) => (
            <Col key={rule.ruleCode} xs={24} sm={12} md={8} lg={6}>
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Text strong>{rule.ruleCode}</Text>
                  <Badge
                    status={rule.isEnabled ? 'success' : 'error'}
                    text={rule.isEnabled ? 'Enabled' : 'Disabled'}
                  />
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {rule.ruleName}
                </Text>
                <div className="mt-2">
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Severity: {rule.defaultSeverity}
                  </Text>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Card>
    </div>
  );

  return (
    <div className="p-4 max-w-full">
      <Helmet>
        <title>STR Monitoring Dashboard | RedCash</title>
      </Helmet>

      <PageHeader
        title="STR Monitoring Dashboard"
        subTitle="Comprehensive suspicious transaction reporting monitoring system"
        extra={[
          <Space key="actions" size="small">
            <RangePicker
              value={
                ui.selectedDateRange
                  ? [dayjs(ui.selectedDateRange[0]), dayjs(ui.selectedDateRange[1])]
                  : undefined
              }
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
              allowClear
            />
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              type="default"
              size="small"
            >
              Export
            </Button>
            <Button icon={<MailOutlined />} onClick={handleSendEmail} type="default" size="small">
              Email
            </Button>
            <Button
              icon={<MailOutlined />}
              onClick={handleSendDailySummary}
              type="default"
              size="small"
            >
              Summary
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadInitialData}
              loading={isRefreshing}
              type="primary"
              size="small"
            >
              Refresh
            </Button>
          </Space>,
        ]}
        className="bg-white mb-4"
      />

      <Spin spinning={dashboardStats.isLoading || rules.isLoading}>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          className="bg-white rounded-lg shadow-sm"
          tabBarStyle={{ padding: '0 16px', marginBottom: 0 }}
          items={[
            {
              key: 'dashboard',
              label: (
                <span>
                  <DashboardOutlined />
                  Dashboard
                </span>
              ),
              children: renderDashboardTab(),
            },
            {
              key: 'alerts',
              label: (
                <span>
                  <AlertOutlined />
                  Alert Management
                  {alerts.data && alerts.data.length > 0 && (
                    <Badge
                      count={alerts.data.filter((a) => a.status === 'ACTIVE').length}
                      offset={[10, 0]}
                      size="small"
                    />
                  )}
                </span>
              ),
              children: <STRAlertManagement />,
            },
            {
              key: 'rules',
              label: (
                <span>
                  <SettingOutlined />
                  Rule Configuration
                </span>
              ),
              children: <STRRuleConfiguration />,
            },
            {
              key: 'monitoring',
              label: (
                <span>
                  <MonitorOutlined />
                  Account Monitoring
                </span>
              ),
              children: <STRAccountMonitoring />,
            },
            // {
            //   key: 'performance',
            //   label: (
            //     <span>
            //       <ArrowUpOutlined />
            //       Performance
            //     </span>
            //   ),
            //   children: <STRPerformanceMonitoring />
            // }
          ]}
        />
      </Spin>
    </div>
  );
};

export default STRDashboard;
